import React, { useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import Button from 'Components/Form/Button'
import SubmitButton from 'Components/Form/SubmitButton'
import { getCities, migrateUser } from 'Module/graphQL'

const MigrateModal = ({
  userId,
  ambassadorName,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [allCities, setAllCities] = useState([])
  const [selectedCountryId, setSelectedCountryId] = useState(null)
  const [selectedCityId, setSelectedCityId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingCities, setIsLoadingCities] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    if (!isOpen) return

    setIsLoadingCities(true)
    getCities()
      .then(response => response.json())
      .then(result => {
        const edges =
          result.data && result.data.cities && result.data.cities.edges
        if (edges) {
          setAllCities(edges.map(({ node }) => node))
        }
        setIsLoadingCities(false)
      })
      .catch(() => {
        setIsLoadingCities(false)
      })
  }, [isOpen])

  const countryOptions = allCities
    .reduce((acc, city) => {
      const country = city.country
      if (!acc.find(c => c.value === country.id)) {
        acc.push({ value: country.id, label: country.name })
      }
      return acc
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label))

  const cityOptions = allCities
    .filter(city => city.country.id === selectedCountryId)
    .map(city => ({ value: city.id, label: city.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const handleCountryChange = value => {
    setSelectedCountryId(value)
    setSelectedCityId(null)
  }

  const handleCityChange = value => {
    setSelectedCityId(value)
  }

  const handleSubmit = async () => {
    if (!selectedCityId) return

    setIsLoading(true)
    setErrorMessage(null)

    try {
      await migrateUser({
        userId: parseInt(userId, 10),
        cityId: parseInt(selectedCityId, 10),
      })
      onSuccess()
    } catch (error) {
      console.error('Migration failed:', error)
      setErrorMessage(
        (error && error.message) || 'Migration failed. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedCountryId(null)
    setSelectedCityId(null)
    setErrorMessage(null)
    onClose()
  }

  const isSubmitDisabled = !selectedCityId || isLoading

  return (
    <Modal show={isOpen} onHide={handleClose}>
      <ModalHeader>
        <ModalTitle>Migrate Ambassador</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Box display="flex" flexDirection="column">
          {ambassadorName && (
            <Box mb={2} style={{ fontSize: 14 }}>
              Migrating: <strong>{ambassadorName}</strong>
            </Box>
          )}
          <Box mb={2}>
            <SelectDashboard
              label="Country"
              input={{
                name: 'country',
                value: selectedCountryId,
                onChange: handleCountryChange,
              }}
              options={isLoadingCities ? [] : countryOptions}
              disabled={isLoadingCities}
            />
          </Box>
          <Box mb={2}>
            <SelectDashboard
              label="City"
              input={{
                name: 'city',
                value: selectedCityId,
                onChange: handleCityChange,
              }}
              options={selectedCountryId ? cityOptions : []}
              disabled={!selectedCountryId}
            />
          </Box>
          {errorMessage && (
            <Box mt={1} style={{ color: 'red', fontSize: 13 }}>
              {errorMessage}
            </Box>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button value="Cancel" onClick={handleClose} />
        <SubmitButton
          value={isLoading ? 'Migrating...' : 'Migrate'}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          hideSvg={true}
        />
      </ModalFooter>
    </Modal>
  )
}

export default MigrateModal
